import { StrColumn, NumColumn } from './dataset.js';
import { parseFilterText } from './query.js';
import { parseDate } from './format.js';
import { setHtml } from './css.js';
import { compileFormula } from './formula.js';
import { compileFilter, describeFilter, emptyGroup, datePresetLabels, datePresetCond, OP_LABEL, STRING_OPS, RANGE_OPS } from './filters.js';
import { esc, onOutsideMouseDown, mountPopup, fixFixedPos } from './grid-util.js';
export class GridFilter {
    setQuickSearch(text) { this.quickText = text; this.rebuild(); }
    setFilter(col, text) { text ? this.filterText.set(col, text) : this.filterText.delete(col); this.syncFilterInputs(); this.rebuild(); }
    setExternalFilter(preds) { this.extFilters = preds; this.rebuild(); }
    clearFilters() { this.filterText.clear(); this.extFilters = []; this.quickText = ''; this.menuFilters.clear(); this.advFilter = null; this.filterEnabled = true; this.findText = ''; this.hits.clear(); this.syncFilterInputs(); this.renderHeader(); this.rebuild(); }
    /** 필터 빌더 트리를 코드로 지정 (QuantumGrid DataController.Filter 에 해당). null 이면 해제 */
    setAdvancedFilter(node) { this.advFilter = node && (node.kind !== 'group' || node.items.length) ? node : null; this.rebuild(); }
    getAdvancedFilter() { return this.advFilter; }
    /** 수식으로 필터 (XPlatform Dataset.filter("dept=='내과' && rslt>=100") 대응). 빈 문자열이면 해제 */
    setFilterExpr(expr) { this.setAdvancedFilter(expr.trim() ? { kind: 'expr', expr } : null); }
    /** 필터 전체를 잠시 끄고 켜기 (필터 패널 체크박스) */
    setFilterEnabled(on) { this.filterEnabled = on; this.rebuild(); }
    isFilterEnabled() { return this.filterEnabled; }
    /** 현재 필터를 사람이 읽는 문장으로 */
    getFilterText() { return this.describeAll(); }
    /** 최근 필터 (MRU) */
    getRecentFilters() { return this.mru.map(m => m.text); }
    applyRecentFilter(i) {
        const m = this.mru[i];
        if (!m)
            return;
        this.menuFilters = new Map(m.menu.map(([k, v]) => [k, { values: v.values ? new Set(v.values) : undefined, cond: v.cond }]));
        this.advFilter = m.adv;
        this.filterText = new Map(m.rowText);
        this.filterEnabled = true;
        this.syncFilterInputs();
        this.renderHeader();
        this.rebuild();
    }
    /** 헤더 필터 지정 (값 목록·조건). null 이면 해제 */
    setColumnFilter(col, f) {
        if (!f || (!f.values && !f.cond))
            this.menuFilters.delete(col);
        else
            this.menuFilters.set(col, { values: f.values ? new Set(f.values) : undefined, cond: f.cond?.preset ? { ...datePresetCond(f.cond.preset), preset: f.cond.preset } : f.cond });
        this.renderHeader();
        this.rebuild();
    }
    getColumnFilters() {
        const o = {};
        for (const [k, v] of this.menuFilters)
            o[k] = { values: v.values ? [...v.values] : undefined, cond: v.cond };
        return o;
    }
    /** 컬럼 값 목록 (다른 필터만 적용한 행 기준) — 필터 메뉴가 쓰는 것과 같다 */
    distinctValues(col, onlyFiltered = true) {
        if (this.srm) { // 서버 값 목록 (openFilterMenu 가 미리 받아 둠)
            const c = this.colInfo(col);
            return (this.srvDistinct.get(col)?.items ?? []).map(it => ({ key: it.key, label: it.key === '' ? this.t('(빈 값)') : this.displayText(c, this.ds.cols[col]?.kind === 'num' ? Number(it.key) : it.key, -1) || it.key, count: it.count }));
        }
        const rows = onlyFiltered ? this.filteredRows(col) : this.ds.allRows();
        const c = this.colInfo(col);
        const dc = this.queryDs().cols[col]; // valueGetter 컬럼은 계산 값 임시 컬럼
        const out = [];
        if (dc instanceof StrColumn) {
            const cnt = new Uint32Array(dc.dict.length);
            for (let i = 0; i < rows.length; i++)
                cnt[dc.codes[rows[i]]]++;
            for (let k = 0; k < cnt.length; k++)
                if (cnt[k]) {
                    const raw = dc.dict[k];
                    out.push({ key: raw ?? '', label: raw == null ? this.t('(빈 값)') : this.displayText(c, raw, -1) || raw, count: cnt[k], sortKey: raw });
                }
            const coll = new Intl.Collator('ko', { numeric: true });
            out.sort((a, b) => (a.sortKey == null ? 1 : b.sortKey == null ? -1 : coll.compare(a.label, b.label)));
        }
        else if (dc instanceof NumColumn) {
            const m = new Map();
            let empty = 0;
            for (let i = 0; i < rows.length; i++) {
                const v = dc.vals[rows[i]];
                if (v !== v)
                    empty++;
                else
                    m.set(v, (m.get(v) ?? 0) + 1);
            }
            for (const [v, n] of [...m].sort((a, b) => a[0] - b[0]))
                out.push({ key: String(v), label: this.displayText(c, dc.type === 'boolean' ? v !== 0 : v, -1), count: n, sortKey: v });
            if (empty)
                out.push({ key: '', label: this.t('(빈 값)'), count: empty, sortKey: null });
        }
        const items = out.map(({ key, label, count }) => ({ key, label, count }));
        return this.opts.onFilterPopupValues ? this.opts.onFilterPopupValues(col, items) : items;
    }
    /** 서버 행 모델 조건 (정렬·필터 행·헤더 필터·코드 필터·빠른 검색). exceptCol 은 값 목록용 */
    getServerQuery(exceptCol) {
        // in/notIn 의 value 배열은 서버 형식(values)으로 (1.2.0: 필터 행 "a,b" 와 조건 메뉴도 — 예전에는 value 그대로 보내 서버가 IN () → 0행)
        const srv = (p) => ((p.op === 'in' || p.op === 'notIn') && Array.isArray(p.value) ? { col: p.col, op: p.op, values: p.value } : p);
        const filter = this.extFilters.filter(p => p.col !== exceptCol).map(srv);
        if (this.filterEnabled) {
            for (const [col, text] of this.filterText) {
                const c = this.colInfo(col);
                if (col === exceptCol || !c)
                    continue;
                const p = parseFilterText(col, this.comboFilterValue(c, text), this.ds.cols[col]?.kind === 'num');
                if (p)
                    filter.push(srv(p));
            }
            for (const [col, f] of this.menuFilters) {
                if (col === exceptCol)
                    continue;
                if (f.cond) {
                    const { preset: _p, ...cd } = f.cond;
                    filter.push(srv({ col, ...cd }));
                }
                if (f.values)
                    filter.push({ col, op: 'in', values: Array.from(f.values) });
            }
        }
        return { sort: this.sortKeys.filter(k => this.ds.cols[k.col]), filter, quick: this.quickText };
    }
    /** 서버 행 모델 상태 (로드된 블록 수 등) */
    getServerState() { return this.srm ? { total: this.srm.total, loadedBlocks: this.srm.loadedBlocks, blockSize: this.srm.blockSize, loading: this.srm.loading, error: this.srm.error } : null; }
    /** 필터 문장이 바뀌었으면 최근 필터에 넣고 이벤트를 보낸다 */
    /** @internal */ afterFilterChange() {
        const text = this.describeAll();
        if (text === this.lastFilterText)
            return;
        this.lastFilterText = text;
        // saveFilters: 저장할 필터가 바뀌면 레이아웃 저장 (복원 중이면 기록만)
        if (this.opts.saveFilters) {
            const sig = JSON.stringify(this.getFilterState());
            if (sig !== this.filterSig) {
                this.filterSig = sig;
                if (!this.applyingFilters)
                    queueMicrotask(() => this.notifyLayout());
            }
        }
        if (text && this.filterEnabled) {
            this.mru = this.mru.filter(m => m.text !== text);
            this.mru.unshift({ text, menu: [...this.menuFilters].map(([k, v]) => [k, { values: v.values ? new Set(v.values) : undefined, cond: v.cond }]), adv: this.advFilter, rowText: [...this.filterText] });
            this.mru.length = Math.min(this.mru.length, 10);
        }
        queueMicrotask(() => this.opts.onFilterChange?.({ text, rows: this.view.length, total: this.ds.length, grid: this }));
    }
    /** 걸린 필터 목록 (도구 패널 "필터" 탭) — remove() 로 그 필터만 뺌 */
    getActiveFilters() {
        const out = [];
        for (const [col, text] of this.filterText)
            out.push({ kind: 'row', col, text: `${this.headerOf(col)}: ${text}`, remove: () => this.setFilter(col, '') });
        for (const [col, f] of this.menuFilters)
            out.push({ kind: 'menu', col, text: this.menuFilterText(col, f).join(' AND '), remove: () => this.setColumnFilter(col, null) });
        if (this.advFilter) {
            const fmt = this.filterFmt();
            out.push({ kind: 'adv', text: describeFilter(this.advFilter, c => this.headerOf(c), fmt, this.t), remove: () => this.setAdvancedFilter(null) });
        }
        if (this.quickText)
            out.push({ kind: 'quick', text: `${this.t('빠른 검색')}: ${this.quickText}`, remove: () => this.setQuickSearch('') });
        if (this.extFilters.length)
            out.push({ kind: 'ext', text: this.t('외부 필터 {n}개', { n: this.extFilters.length }), remove: () => this.setExternalFilter([]) });
        return out;
    }
    /** @internal */ filterFmt() { return (col, v) => { const c = this.colInfo(col); return c && v != null && v !== '' ? this.displayText(c, v, -1) || String(v) : v === '' ? this.t('(빈 값)') : String(v ?? ''); }; }
    /** @internal */ menuFilterText(col, f) {
        const parts = [], fmt = this.filterFmt();
        if (f.cond)
            parts.push(describeFilter({ kind: 'cond', col, ...f.cond }, c => this.headerOf(c), fmt, this.t));
        if (f.values) {
            const v = [...f.values];
            const shown = v.slice(0, 3).map(x => fmt(col, x === '' ? null : x) || this.t('(빈 값)')).join(', ');
            parts.push(`${this.headerOf(col)} ∈ (${shown}${v.length > 3 ? this.t(' 외 {n}개', { n: v.length - 3 }) : ''})`);
        }
        return parts;
    }
    /** @internal */ headerOf(col) { return this.colInfo(col)?.def.header ?? col; }
    /** @internal */ describeAll() {
        const parts = [];
        const fmt = (col, v) => { const c = this.colInfo(col); return c && v != null && v !== '' ? this.displayText(c, v, -1) || String(v) : v === '' ? this.t('(빈 값)') : String(v ?? ''); };
        for (const [col, text] of this.filterText)
            parts.push(`${this.headerOf(col)}: ${text}`);
        for (const [col, f] of this.menuFilters)
            parts.push(...this.menuFilterText(col, f));
        if (this.advFilter)
            parts.push(describeFilter(this.advFilter, c => this.headerOf(c), fmt, this.t));
        return parts.join(' AND ');
    }
    /** @internal */ renderFilterPanel() {
        const mode = this.opts.showFilterPanel ?? 'auto';
        const text = this.describeAll();
        const show = mode === true || (mode === 'auto' && (!!text || !this.filterEnabled));
        this.fpanel.hidden = !show;
        if (!show)
            return;
        setHtml(this.fpanel, `<label class="og-fp-on" title="${this.t('필터 켜기/끄기')}"><input type="checkbox" data-a="toggle"${this.filterEnabled ? ' checked' : ''}></label>
<button type="button" class="og-fp-text" data-a="edit" title="${this.t('필터 편집')}">${esc(text || this.t('(필터 없음)'))}</button>
${this.mru.length ? `<select data-a="mru" aria-label="${this.t('최근 필터')}"><option value="">${this.t('최근 필터…')}</option>${this.mru.map((m, i) => `<option value="${i}">${esc(m.text.length > 60 ? m.text.slice(0, 58) + '…' : m.text)}</option>`).join('')}</select>` : ''}
<button type="button" data-a="edit">${this.t('편집…')}</button><button type="button" data-a="clear" title="${this.t('필터 모두 해제')}">✕</button>`);
        this.fpanel.classList.toggle('og-fp-off', !this.filterEnabled);
    }
    /** @internal */ syncFilterInputs() { this.root.querySelectorAll('.og-fi').forEach(i => { i.value = this.filterText.get(i.dataset.col) ?? ''; }); }
    /** @internal */ onFilterInput(e) {
        const t = e.target;
        if (!t.classList.contains('og-fi'))
            return;
        clearTimeout(this.filterTimer);
        this.filterTimer = window.setTimeout(() => {
            const v = t.value.trim();
            v ? this.filterText.set(t.dataset.col, v) : this.filterText.delete(t.dataset.col);
            this.rebuild();
        }, 180);
    }
    // ═══════════════════════ 헤더 필터 메뉴 ═══════════════════════
    closeFilterMenu() { this.menuClose?.(); this.menuClose = null; this.menuEl?.remove(); this.menuEl = null; }
    /** 엑셀식 필터: 정렬, 조건(전체 연산자·날짜 프리셋), 값 체크 목록(건수·필터된 값만), 검색, 최근 필터 */
    openFilterMenu(col) {
        this.closeFilterMenu();
        if (this.srm) {
            const q = this.getServerQuery(col), sig = JSON.stringify(q);
            if (this.srvDistinct.get(col)?.sig !== sig) {
                this.srm.distinct(col, q.filter, q.quick).then(items => { this.srvDistinct.set(col, { sig, items }); this.openFilterMenu(col); }).catch(e => { this.showTip(String(e.message ?? e)); setTimeout(() => this.hideTip(), 2500); });
                return;
            }
        }
        this.commitEdit();
        const c = this.colInfo(col);
        if (!c)
            return;
        const dc = this.queryDs().cols[col];
        const isDate = dc?.type === 'date' || c.def.format === 'date' || c.def.format === 'datetime';
        const ranged = dc?.kind === 'num' || isDate;
        const cur = this.menuFilters.get(col);
        let onlyFiltered = true;
        let items = this.distinctValues(col, onlyFiltered);
        const ops = ['', ...(ranged ? RANGE_OPS : STRING_OPS)];
        const recent = this.colMru.get(col) ?? [];
        const m = document.createElement('div');
        m.className = 'og-menu' + (this.touchOn ? ' og-tmenu' : '');
        m.setAttribute('role', 'dialog');
        m.setAttribute('aria-label', this.t('{col} 필터', { col: String(c.def.header ?? col) }));
        setHtml(m, `
<div class="og-mrow"><button type="button" data-a="asc">${this.t('↑ 오름차순')}</button><button type="button" data-a="desc">${this.t('↓ 내림차순')}</button></div>
${isDate ? `<select data-f="preset" aria-label="${this.t('날짜 프리셋')}"><option value="">${this.t('날짜 빠른 선택…')}</option>${datePresetLabels(this.t).map(([k, l]) => `<option value="${k}">${esc(l)}</option>`).join('')}</select>` : ''}
<div class="og-mrow og-mcond"><select data-f="op" aria-label="${this.t('조건')}">${ops.map(v => `<option value="${v}"${cur?.cond?.op === v ? ' selected' : ''}>${v ? this.t(OP_LABEL[v]) : this.t('조건 없음')}</option>`).join('')}</select>
<input data-f="v1" aria-label="${this.t('조건 값')}" value="${esc(Array.isArray(cur?.cond?.value) ? cur.cond.value.join(',') : String(cur?.cond?.value ?? ''))}"><input data-f="v2" aria-label="${this.t('범위 끝 값')}" placeholder="~" value="${esc(String(cur?.cond?.value2 ?? ''))}"></div>
${recent.length ? `<div class="og-mrecent">${this.t('최근')}: ${recent.map((r, i) => `<button type="button" data-r="${i}" title="${esc(r.text)}">${esc(r.text.length > 18 ? r.text.slice(0, 17) + '…' : r.text)}</button>`).join('')}</div>` : ''}
<input class="og-msearch" data-f="q" placeholder="${this.t('값 검색')}" aria-label="${this.t('값 검색')}">
<div class="og-mrow og-mopts"><label class="og-mall"><input type="checkbox" data-f="all"> ${this.t('(전체 선택)')}</label><label class="og-monly" title="${this.t('다른 필터에 걸러진 값만 보기')}"><input type="checkbox" data-f="only" checked> ${this.t('필터된 값만')}</label></div>
<div class="og-mlist" role="listbox"></div>
<div class="og-mrow og-mbtns"><button type="button" class="og-mok" data-a="apply">${this.t('적용')}</button><button type="button" data-a="reset">${this.t('초기화')}</button><button type="button" data-a="close">${this.t('닫기')}</button></div>`);
        mountPopup(this, m);
        this.menuEl = m;
        const $ = (sel) => m.querySelector(sel);
        const q = $('[data-f=q]'), all = $('[data-f=all]'), only = $('[data-f=only]');
        const list = m.querySelector('.og-mlist');
        const opSel = m.querySelector('[data-f=op]');
        const preset = m.querySelector('[data-f=preset]');
        const v1 = $('[data-f=v1]'), v2 = $('[data-f=v2]');
        const checked = new Set(cur?.values ?? items.map(i => i.key));
        const LIMIT = 1000;
        let shown = items;
        const syncOp = () => {
            const op = opSel.value;
            v1.hidden = !op || op === 'empty' || op === 'notEmpty';
            v2.hidden = op !== 'between' && op !== 'notBetween';
            v1.placeholder = op === 'in' || op === 'notIn' ? this.t('값1,값2,…') : op === 'like' || op === 'notLike' ? this.t('예') + ': ' + this.t('아목*캡?') : isDate ? 'YYYY-MM-DD' : '';
            if (isDate)
                v2.placeholder = 'YYYY-MM-DD';
        };
        const draw = () => {
            const s = q.value.trim().toLowerCase();
            shown = s ? items.filter(i => i.label.toLowerCase().includes(s) || i.key.toLowerCase().includes(s)) : items;
            q.placeholder = this.t('값 검색 ({n}개)', { n: items.length.toLocaleString('ko-KR') });
            setHtml(list, shown.slice(0, LIMIT).map((it, i) => `<label class="og-mi"><input type="checkbox" data-i="${i}"${checked.has(it.key) ? ' checked' : ''}><span>${esc(it.label)}</span><em>${it.count.toLocaleString('ko-KR')}</em></label>`).join(''))
                + (shown.length > LIMIT ? `<div class="og-mmore">${this.t('{n}개 더 있음 — 검색으로 좁히세요', { n: (shown.length - LIMIT).toLocaleString('ko-KR') })}</div>` : '')
                + (shown.length ? '' : '<div class="og-mmore">' + this.t('일치하는 값 없음') + '</div>');
            const n = shown.filter(i => checked.has(i.key)).length;
            all.checked = n === shown.length && n > 0;
            all.indeterminate = n > 0 && n < shown.length;
        };
        syncOp();
        draw();
        list.addEventListener('change', e => {
            const t = e.target;
            const it = shown[+t.dataset.i];
            if (!it)
                return;
            t.checked ? checked.add(it.key) : checked.delete(it.key);
            const n = shown.filter(i => checked.has(i.key)).length;
            all.checked = n === shown.length;
            all.indeterminate = n > 0 && n < shown.length;
        });
        all.addEventListener('change', () => { for (const it of shown)
            all.checked ? checked.add(it.key) : checked.delete(it.key); draw(); });
        only.addEventListener('change', () => { onlyFiltered = only.checked; const before = new Set(items.map(i => i.key)); items = this.distinctValues(col, onlyFiltered); for (const it of items)
            if (!before.has(it.key) && !cur?.values)
                checked.add(it.key); draw(); });
        q.addEventListener('input', draw);
        opSel.addEventListener('change', () => { syncOp(); if (!v1.hidden)
            v1.focus(); });
        preset?.addEventListener('change', () => {
            if (!preset.value)
                return;
            const pc = datePresetCond(preset.value);
            opSel.value = pc.op;
            v1.value = pc.value;
            v2.value = pc.value2 ?? '';
            syncOp();
        });
        const apply = () => {
            const op = opSel.value;
            let cond;
            if (op) {
                const conv = (x) => (dc?.kind === 'num' ? Number(x.replace(/,/g, '')) : isDate ? (parseDate(x) ?? x) : x);
                if (op === 'empty' || op === 'notEmpty')
                    cond = { op };
                else if (op === 'in' || op === 'notIn') {
                    if (v1.value.trim())
                        cond = { op, value: v1.value.split(',').map(x => x.trim()).filter(Boolean).map(conv) };
                }
                else if (v1.value.trim())
                    cond = { op, value: conv(v1.value.trim()), value2: op === 'between' || op === 'notBetween' ? conv(v2.value.trim()) : undefined };
                // 날짜 빠른 선택(이번 주 등)을 그대로 쓰면 이름을 붙여 둠 → 레이아웃 복원 때 그날 기준으로 다시 계산
                if (cond && preset?.value) {
                    const pc = datePresetCond(preset.value);
                    if (pc.op === cond.op && pc.value === cond.value && (pc.value2 ?? undefined) === (cond.value2 ?? undefined))
                        cond.preset = preset.value;
                }
            }
            // 검색 중이면 검색 결과 가운데 체크한 값만 남긴다 (엑셀과 같음)
            const s = q.value.trim();
            const keys = s ? shown.filter(i => checked.has(i.key)).map(i => i.key) : items.filter(i => checked.has(i.key)).map(i => i.key);
            const allSelected = !s && keys.length === items.length;
            const f = { values: allSelected ? undefined : keys, cond };
            this.closeFilterMenu();
            this.setColumnFilter(col, f);
            if (f.values || f.cond) {
                const text = describeFilter({ kind: 'cond', col, op: f.cond?.op ?? 'in', value: f.cond?.value ?? f.values, value2: f.cond?.value2 }, () => '', (_c, v) => String(v)).trim();
                const lst = (this.colMru.get(col) ?? []).filter(r => r.text !== text);
                lst.unshift({ text, f });
                this.colMru.set(col, lst.slice(0, 5));
            }
            this.focusGrid();
        };
        m.addEventListener('click', e => {
            const b = e.target.closest('button');
            if (!b)
                return;
            if (b.dataset.r != null) {
                const r = recent[+b.dataset.r];
                this.closeFilterMenu();
                this.setColumnFilter(col, r.f);
                this.focusGrid();
                return;
            }
            const a = b.dataset.a;
            if (a === 'apply')
                apply();
            else if (a === 'reset') {
                this.closeFilterMenu();
                this.setColumnFilter(col, null);
                this.focusGrid();
            }
            else if (a === 'close') {
                this.closeFilterMenu();
                this.focusGrid();
            }
            else if (a === 'asc' || a === 'desc') {
                this.closeFilterMenu();
                this.setSort([{ col, dir: a }]);
                this.focusGrid();
            }
        });
        m.addEventListener('keydown', e => {
            if (e.key === 'Escape') {
                e.preventDefault();
                this.closeFilterMenu();
                this.focusGrid();
            }
            else if (e.key === 'Enter' && e.target.tagName !== 'BUTTON') {
                e.preventDefault();
                apply();
            }
        });
        this.placePopup(m, this.root.querySelector(`.og-hc[data-ci="${c.ci}"]`));
        const outside = (e) => { if (!m.contains(e.target) && !e.target.classList?.contains('og-fb'))
            this.closeFilterMenu(); };
        this.menuClose = onOutsideMouseDown(outside);
        (opSel.value ? v1 : q).focus();
    }
    /** @internal */ placePopup(m, anchor) {
        const hb = (anchor ?? this.head).getBoundingClientRect();
        const mw = m.offsetWidth, mh = m.offsetHeight;
        m.style.left = Math.max(4, Math.min(hb.left, window.innerWidth - mw - 4)) + 'px';
        m.style.top = Math.max(4, Math.min(hb.bottom + 2, window.innerHeight - mh - 4)) + 'px';
        fixFixedPos(m);
    }
    // ═══════════════════════ 필터 빌더 (AND/OR/NOT, 수식) ═══════════════════════
    closeFilterBuilder() { this.builderEl?.remove(); this.builderEl = null; }
    openFilterBuilder() {
        this.closeFilterMenu();
        this.closeFilterBuilder();
        // 현재 헤더 필터 + 빌더 필터를 하나의 트리로 모아서 편집
        let tree = this.advFilter && this.advFilter.kind === 'group' ? structuredClone(this.advFilter) : emptyGroup();
        let exprText = this.advFilter?.kind === 'expr' ? this.advFilter.expr : '';
        if (this.advFilter && this.advFilter.kind === 'cond')
            tree.items.push(structuredClone(this.advFilter));
        for (const [col, f] of this.menuFilters) {
            if (f.cond)
                tree.items.push({ kind: 'cond', col, ...f.cond });
            if (f.values)
                tree.items.push({ kind: 'cond', col, op: 'in', value: [...f.values].join(',') });
        }
        const cols = this.cols.filter(c => !c.special && (!c.def.value || (c.def.valueGetter && !this.srm)));
        const isRanged = (col) => { const d = this.queryDs().cols[col]; const c = this.colInfo(col); return d?.kind === 'num' || d?.type === 'date' || c?.def.format === 'date'; };
        const b = document.createElement('div');
        b.className = 'og-builder';
        b.setAttribute('role', 'dialog');
        b.setAttribute('aria-label', this.t('필터 편집'));
        mountPopup(this, b);
        this.builderEl = b;
        let tab = exprText ? 'expr' : 'tree';
        const condHtml = (n, path) => {
            const ops = isRanged(n.col) ? RANGE_OPS : STRING_OPS;
            const two = n.op === 'between' || n.op === 'notBetween', none = n.op === 'empty' || n.op === 'notEmpty';
            return `<div class="og-bcond" data-p="${path}"><label class="og-bnot" title="${this.t('부정')}"><input type="checkbox" data-k="not"${n.not ? ' checked' : ''}>NOT</label>
<select data-k="col" aria-label="${this.t('컬럼')}">${cols.map(c => `<option value="${esc(c.id)}"${c.id === n.col ? ' selected' : ''}>${esc(c.def.header ?? c.id)}</option>`).join('')}</select>
<select data-k="op" aria-label="${this.t('연산자')}">${ops.map(o => `<option value="${o}"${o === n.op ? ' selected' : ''}>${this.t(OP_LABEL[o])}</option>`).join('')}</select>
${none ? '' : `<input data-k="value" aria-label="${this.t('값')}" value="${esc(Array.isArray(n.value) ? n.value.join(',') : String(n.value ?? ''))}">`}${two ? `<input data-k="value2" aria-label="${this.t('끝 값')}" value="${esc(String(n.value2 ?? ''))}">` : ''}
<button type="button" data-b="del" title="${this.t('조건 삭제')}">✕</button></div>`;
        };
        const groupHtml = (g, path) => `<div class="og-bgroup" data-p="${path}"><div class="og-bghead"><select data-k="logic" aria-label="${this.t('결합')}"><option value="AND"${g.logic === 'AND' ? ' selected' : ''}>${this.t('모두 만족 (AND)')}</option><option value="OR"${g.logic === 'OR' ? ' selected' : ''}>${this.t('하나 이상 (OR)')}</option></select>
<label class="og-bnot"><input type="checkbox" data-k="not"${g.not ? ' checked' : ''}>NOT</label><button type="button" data-b="addc">${this.t('+ 조건')}</button><button type="button" data-b="addg">${this.t('+ 그룹')}</button>${path ? '<button type="button" data-b="del">' + this.t('그룹 삭제') + '</button>' : ''}</div>
<div class="og-bitems">${g.items.map((it, i) => (it.kind === 'group' ? groupHtml(it, `${path}${path ? '.' : ''}${i}`) : it.kind === 'cond' ? condHtml(it, `${path}${path ? '.' : ''}${i}`) : `<div class="og-bcond" data-p="${path}.${i}">${this.t('수식')}: ${esc(it.expr)} <button type="button" data-b="del">✕</button></div>`)).join('') || `<div class="og-bempty">${esc(this.t('조건이 없습니다. "+ 조건"을 누르세요.'))}</div>`}</div></div>`;
        const nodeAt = (path) => path === '' ? tree : path.split('.').reduce((n, i) => n.items[+i], tree);
        const parentOf = (path) => { const p = path.split('.'); const i = +p.pop(); return { parent: nodeAt(p.join('.')), i }; };
        const preview = () => {
            try {
                const node = tab === 'expr' ? { kind: 'expr', expr: exprText } : tree;
                if (tab === 'expr' && !exprText.trim())
                    return this.t('수식을 입력하세요');
                if (tab === 'expr')
                    compileFormula(exprText);
                const f = compileFilter(this.ds, node);
                let n = 0;
                const all = this.ds.allRows();
                for (let i = 0; i < all.length; i++)
                    if (f(all[i]))
                        n++;
                return `${describeFilter(node, c => this.headerOf(c), undefined, this.t) || this.t('(조건 없음)')}  →  ${this.t('{n} / {total}건', { n: n.toLocaleString('ko-KR'), total: all.length.toLocaleString('ko-KR') })}`;
            }
            catch (e) {
                return this.t('오류') + ': ' + e.message;
            }
        };
        const draw = () => {
            setHtml(b, `<div class="og-bhead"><b>${this.t('필터 편집')}</b><div class="og-btabs"><button type="button" data-t="tree" aria-pressed="${tab === 'tree'}">${this.t('조건')}</button><button type="button" data-t="expr" aria-pressed="${tab === 'expr'}">${this.t('수식')}</button></div></div>
<div class="og-bbody">${tab === 'tree' ? groupHtml(tree, '') : `<textarea data-k="expr" rows="5" aria-label="${this.t('필터 수식')}" placeholder="${esc(this.t('예'))}: ${esc(this.t("dept = '내과' AND rslt >= 100"))}&#10;IF(flag = 'H', 1, 0) = 1&#10;DATEDIFF(rsltDt, TODAY()) <= 7">${esc(exprText)}</textarea><div class="og-bhelp">${this.t('컬럼 id')}: ${cols.map(c => `${esc(c.id)}(${esc(c.def.header ?? '')})`).join(', ')}</div>`}</div>
<div class="og-bprev" aria-live="polite">${esc(preview())}</div>
<div class="og-mrow og-mbtns"><button type="button" class="og-mok" data-b="apply">${this.t('적용')}</button><button type="button" data-b="reset">${this.t('모두 지우기')}</button><button type="button" data-b="close">${this.t('취소')}</button></div>`);
        };
        draw();
        b.addEventListener('click', e => {
            const t = e.target;
            const tb = t.closest('[data-t]');
            if (tb) {
                tab = tb.dataset.t;
                draw();
                return;
            }
            const btn = t.closest('[data-b]');
            if (!btn)
                return;
            const act = btn.dataset.b;
            const path = btn.closest('[data-p]')?.dataset.p ?? '';
            if (act === 'addc') {
                nodeAt(path).items.push({ kind: 'cond', col: cols[0]?.id, op: isRanged(cols[0]?.id) ? '>=' : 'contains', value: '' });
                draw();
            }
            else if (act === 'addg') {
                nodeAt(path).items.push({ kind: 'group', logic: 'OR', items: [] });
                draw();
            }
            else if (act === 'del') {
                const { parent, i } = parentOf(path);
                parent.items.splice(i, 1);
                draw();
            }
            else if (act === 'reset') {
                tree = emptyGroup();
                exprText = '';
                draw();
            }
            else if (act === 'close') {
                this.closeFilterBuilder();
                this.focusGrid();
            }
            else if (act === 'apply') {
                let node;
                if (tab === 'expr') {
                    try {
                        compileFormula(exprText);
                    }
                    catch (err) {
                        b.querySelector('.og-bprev').textContent = this.t('오류') + ': ' + err.message;
                        return;
                    }
                    node = exprText.trim() ? { kind: 'expr', expr: exprText } : null;
                }
                else {
                    prune(tree);
                    node = tree.items.length ? tree : null;
                }
                this.menuFilters.clear();
                this.closeFilterBuilder();
                this.renderHeader();
                this.setAdvancedFilter(node);
                this.focusGrid();
            }
        });
        const onEdit = (e) => {
            const t = e.target;
            const k = t.dataset.k;
            if (!k)
                return;
            if (k === 'expr') {
                exprText = t.value;
                b.querySelector('.og-bprev').textContent = preview();
                return;
            }
            const path = t.closest('[data-p]').dataset.p;
            const n = nodeAt(path);
            if (k === 'not')
                n.not = t.checked;
            else if (k === 'logic')
                n.logic = t.value;
            else if (k === 'col') {
                n.col = t.value;
                const ops = isRanged(n.col) ? RANGE_OPS : STRING_OPS;
                if (!ops.includes(n.op))
                    n.op = ops[0];
            }
            else if (k === 'op')
                n.op = t.value;
            else if (k === 'value' || k === 'value2') {
                const d = this.ds.cols[n.col];
                n[k] = d?.kind === 'num' && n.op !== 'in' && n.op !== 'notIn' && t.value.trim() !== '' ? Number(t.value.replace(/,/g, '')) : t.value;
            }
            if (e.type === 'change' && (k === 'col' || k === 'op'))
                draw();
            else
                b.querySelector('.og-bprev').textContent = preview();
        };
        b.addEventListener('change', onEdit);
        b.addEventListener('input', onEdit);
        b.addEventListener('keydown', e => { if (e.key === 'Escape') {
            this.closeFilterBuilder();
            this.focusGrid();
        } });
        const prune = (g) => { g.items = g.items.filter((it) => it.kind !== 'cond' || it.op === 'empty' || it.op === 'notEmpty' || (it.value !== '' && it.value != null)); for (const it of g.items)
            if (it.kind === 'group')
                prune(it); g.items = g.items.filter((it) => it.kind !== 'group' || it.items.length); };
    }
}
